import { useRef, useMemo } from "react"
import { Link } from "react-router-dom"
import { ExternalLink } from "lucide-react"
import { CellContext, PaginationState } from "@tanstack/react-table"
import Table from "../../table"
import DateFilter, {
  DateFilterHandle,
  DateRangePreset
} from "../../activity/date-filter"
import { BBColumnDef, WebsiteChatSession } from "../../../../types"
import { formatAdvisorDate } from "../../../utils/datetime"

interface Props {
  sessions: WebsiteChatSession[]
  isLoading: boolean
  totalRows: number
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  pageCount: number
  datePreset: DateRangePreset
  customDateStart: number | null
  customDateEnd: number | null
  onDateChange: (
    range: DateRangePreset,
    startDate?: Date,
    endDate?: Date
  ) => void
  onClearDate: () => void
}

const WebsiteChatsTable = ({
  sessions,
  isLoading,
  totalRows,
  pagination,
  setPagination,
  pageCount,
  datePreset,
  customDateStart,
  customDateEnd,
  onDateChange,
  onClearDate
}: Props) => {
  const dateFilterRef = useRef<DateFilterHandle>(null)

  const columns = useMemo<BBColumnDef<WebsiteChatSession>[]>(
    () => [
      {
        id: "sessionId",
        header: "Session ID",
        accessorFn: row => row.subjectId,
        cell: ({ row }: CellContext<WebsiteChatSession, unknown>) => (
          <td className="py-4 pl-2 pr-3">
            <span className="font-mono text-xs text-neutral-500 truncate max-w-xs block">
              {row.original.subjectId}
            </span>
          </td>
        )
      },
      {
        id: "lastActivity",
        header: "Last Activity",
        accessorFn: row => row.lastActivityAt,
        cell: ({ row }: CellContext<WebsiteChatSession, unknown>) => (
          <td className="py-4 pr-3 text-sm text-neutral-500 whitespace-nowrap">
            {formatAdvisorDate(row.original.lastActivityAt)}
          </td>
        ),
        meta: { headerClassName: "hidden md:table-cell" }
      },
      {
        id: "threads",
        header: "Threads #",
        accessorFn: row => row.threads.length,
        cell: ({ row }: CellContext<WebsiteChatSession, unknown>) => (
          <td className="py-4 pr-3 text-sm text-neutral-500">
            {row.original.threads.length}
          </td>
        ),
        meta: { headerClassName: "hidden md:table-cell" },
        enableSorting: true
      },
      {
        id: "action",
        header: "",
        enableSorting: false,
        meta: { headerClassName: "w-12 sr-only" },
        cell: ({ row }: CellContext<WebsiteChatSession, unknown>) => (
          <td className="px-5 py-3 text-right">
            <Link
              target="_blank"
              to={`/conversations/advisor/${row.original._id}`}
              className="inline-flex text-neutral-400 hover:text-purple-400 transition-colors"
              title="Open in Conversations"
            >
              <ExternalLink className="w-4 h-4" />
            </Link>
          </td>
        )
      }
    ],
    []
  )

  return (
    <Table<WebsiteChatSession>
      isLoading={isLoading}
      data={sessions}
      columns={columns}
      viewType="table"
      pagination={pagination}
      setPagination={setPagination}
      pageCount={pageCount}
      rowCount={totalRows}
      pageSize={pagination.pageSize}
      extraHeaderElement={
        <DateFilter
          ref={dateFilterRef}
          selectedRange={datePreset}
          customStartDate={customDateStart ? new Date(customDateStart) : null}
          customEndDate={customDateEnd ? new Date(customDateEnd) : null}
          onRangeChange={onDateChange}
          onClearFilter={onClearDate}
        />
      }
      extraHeaderElementClassName="ml-auto"
      shrinkFilterSort
      defaultSortBy="lastActivity"
      defaultSortDir="desc"
      noSorter={false}
    />
  )
}

export default WebsiteChatsTable
